import { Inject, Injectable, Injector, TypeProvider } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  defaultConfigState,
  IApiUrl,
  IAppConfig,
} from '@nibtravel/shared/util/models';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { APP_CONFIG_SVC, IAppConfigService } from '../models';
import { LoadAppConfig } from './app-config.actions';

@State<IAppConfig>({
  name: 'appConfig',
  defaults: defaultConfigState,
})
@Injectable({ providedIn: 'root' })
export class AppConfigState {
  configService: IAppConfigService;
  constructor(
    @Inject(APP_CONFIG_SVC) configSvcType: TypeProvider,
    injector: Injector
  ) {
    this.configService = injector.get(configSvcType);
  }

  @Selector()
  public static getState(state: IAppConfig): IAppConfig {
    return state;
  }

  @Selector()
  public static getTheme(state: IAppConfig): string {
    return state.theme as string;
  }

  @Selector()
  public static getBrand(state: IAppConfig): string {
    return state.brand;
  }

  @Selector()
  public static getPartner(state: IAppConfig): string {
    return state.partner as string;
  }

  @Selector()
  public static campaignCode(state: IAppConfig): string | undefined {
    return state.campaignCode;
  }

  @Selector()
  public static partner(state: IAppConfig): string | undefined {
    return state.partner;
  }

  @Selector()
  public static isProd(state: IAppConfig): boolean {
    return state.production;
  }

  @Selector()
  public static logo(state: IAppConfig): any {
    return state.logo;
  }

  @Selector()
  public static getThemeConfig(state: IAppConfig) {
    return {
      brand: state.brand,
      theme: state.theme,
      logo: state.logo,
      title: state.title,
    };
  }

  /**
   *
   * @param state
   * @param key
   * @description return api full url with version
   */
  @Selector()
  public static getApiFullUrl(state: IAppConfig, key: string): string {
    return state ? (state[key] as IApiUrl)?.fullUrl || '' : '';
  }

  /**
   * @description returns api url without version
   */
  @Selector()
  public static getApiUrl(state: IAppConfig, key: string): string {
    return state ? (state[key] as IApiUrl)?.url : '';
  }

  @Selector()
  public static getAppUrl(state: IAppConfig): string {
    return state?.purchasePath ?? '';
  }

  @Selector()
  public static getKosmosUrl(state: IAppConfig): string {
    return state?.kosmos?.url ?? '';
  }

  @Selector()
  public static getNewRelic(state: IAppConfig): string | undefined {
    return state?.newRelic;
  }

  /**
   * Basic selector to get the current runtime environment
   *
   * @static
   * @param {IAppConfig} state
   * @returns {string}
   * @memberof AppConfigState
   */
  @Selector()
  public static getRuntime(state: IAppConfig): string {
    return state.stage;
  }

  @Selector()
  public static getContentfulConfig(state: IAppConfig) {
    return state.contentful;
  }

  /**
   * @description returns tealium script address
   */
  @Selector()
  public static getTealiumScriptAddress(state: IAppConfig): string {
    return state?.tealium?.script ?? '';
  }

  /**
   * The main action for the app config state.
   * It loads the configuration file
   *
   * @param {StateContext<IAppConfig>} ctx
   * @returns {Promise<IAppConfig>}
   * @memberof AppConfigState
   */
  @Action(LoadAppConfig)
  public load(ctx: StateContext<IAppConfig>): Observable<IAppConfig> {
    return this.configService.loadBrandConfig().pipe(
      tap((config) => {
        ctx.patchState({ ...ctx.getState(), ...config });
      })
    );
  }
}
